import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import GetAccessModal from '@/components/landing/GetAccessModal';
import { ThemeContext } from '@/contexts/ThemeContext';
import { tLanding, type LandingLang, type LandingTranslationKey } from '@/i18n/landingTranslations';
import { LandingHero } from '@/components/landingPrototype/LandingHero';
import { AboutSwarmindSection } from '@/components/landingPrototype/AboutSwarmindSection';
import { MorePossibilitiesSection } from '@/components/landingPrototype/MorePossibilitiesSection';
import { ModelsTableSection } from '@/components/landingPrototype/ModelsTableSection';
import { FaqSidebar } from '@/components/landingPrototype/FaqSidebar';
import { LandingFooter } from '@/components/landingPrototype/LandingFooter';
import { LandingTopNav } from '@/components/landingPrototype/LandingTopNav';
import { LandingSeo } from '@/components/seo/LandingSeo';

import '@/styles/landingPrototype.css';

const LandingPage: React.FC = () => {
  const { isDark, setIsDark } = useContext(ThemeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [isAccessOpen, setIsAccessOpen] = useState(false);
  const [isFaqCollapsed, setIsFaqCollapsed] = useState(true);
  const [language, setLanguage] = useState<'EN' | 'RU'>(() => {
    try {
      const stored = (localStorage.getItem('language') || 'en').toLowerCase();
      return stored === 'ru' ? 'RU' : 'EN';
    } catch {
      return 'EN';
    }
  });

  const lang: LandingLang = language === 'RU' ? 'ru' : 'en';
  const t = (key: LandingTranslationKey) => tLanding(lang, key);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  const toggleLanguage = () => {
    const next = language === 'EN' ? 'RU' : 'EN';
    setLanguage(next);
    try {
      localStorage.setItem('language', next.toLowerCase());
    } catch {
      // ignore
    }
  };

  return (
    <div
      className={`swarmLandingRoot ${isDark ? '' : 'light-theme'}`}
      style={
        {
          ['--faq-offset' as any]: isFaqCollapsed ? '64px' : '360px',
          ['--page-accent' as any]: '#7a80d0',
          ['--page-accent-dark' as any]: '#4d52b0',
        } as React.CSSProperties
      }
    >
      <LandingSeo
        title={t('heroTitle')}
        description={t('heroSubtitle')}
        path={location.pathname === '/landing' ? '/landing' : '/'}
        lang={lang}
      />

      <FaqSidebar t={t} accentColor="#7a80d0" onCollapsedChange={setIsFaqCollapsed} />
      <LandingTopNav t={t} />

      <div className="landing-main">
        <LandingHero
          t={t}
          onGetAccess={() => setIsAccessOpen(true)}
          onLogin={() => navigate('/login')}
        />
        <AboutSwarmindSection t={t} onGetAccess={() => setIsAccessOpen(true)} />
        <MorePossibilitiesSection t={t} onGetAccess={() => setIsAccessOpen(true)} />
        <ModelsTableSection t={t} />
      </div>

      <LandingFooter
        t={t}
        language={language}
        onToggleLanguage={toggleLanguage}
        isLightTheme={!isDark}
        onToggleTheme={() => setIsDark(!isDark)}
        onGetAccess={() => setIsAccessOpen(true)}
      />

      <GetAccessModal lang={lang} isOpen={isAccessOpen} onClose={() => setIsAccessOpen(false)} />
    </div>
  );
};

export default LandingPage;


